import React, { useCallback, useId, useMemo, useRef } from "react";

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/** Photoroom 0°=behind (top) → screen atan2 radians (y-down). */
function directionToScreenRad(directionDeg) {
  const screenDeg = (((-90 - directionDeg) % 360) + 360) % 360;
  return (screenDeg * Math.PI) / 180;
}

function screenPointerToDirectionDeg(dx, dy) {
  const screenDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
  return Math.round((((-90 - screenDeg) % 360) + 360) % 360);
}

/**
 * Light on the disc: angle = direction, radius from elevation (pose).
 * Elevation 90 (overhead) → near center; 0 → near rim.
 */
function lightDiscPosition(directionDeg, poseDeg, cx, cy, radius) {
  const az = directionToScreenRad(directionDeg);
  const rNorm = clamp(1 - poseDeg / 90, 0.08, 1);
  return {
    x: cx + radius * rNorm * Math.cos(az),
    y: cy + radius * rNorm * Math.sin(az),
  };
}

function pointerToLight(dx, dy, radius) {
  const dist = Math.hypot(dx, dy);
  const rNorm = dist < 1e-6 ? 0.55 : clamp(dist / radius, 0.08, 1);
  const directionDeg =
    dist < 1e-6 ? 180 : screenPointerToDirectionDeg(dx, dy);
  const poseDeg = Math.round((1 - rNorm) * 90);
  return { directionDeg, poseDeg };
}

/**
 * Space-fabric / matrix mesh on a sphere.
 * Soft warp pulls the grid toward the light (like curved spacetime).
 */
function buildFabricMesh(cx, cy, r, lightX, lightY) {
  const lx = clamp((lightX - cx) / (r || 1), -1.2, 1.2);
  const ly = clamp((lightY - cy) / (r || 1), -1.2, 1.2);
  const warp = 0.18;

  const project = (theta, phi) => {
    let x = Math.cos(phi) * Math.cos(theta);
    let y = Math.sin(phi);
    let z = Math.cos(phi) * Math.sin(theta);

    const pull = warp * (0.35 + 0.65 * ((z + 1) * 0.5));
    x += lx * pull * 0.42;
    y += ly * pull * 0.42;

    const len = Math.sqrt(x * x + y * y + z * z) || 1;
    x /= len;
    y /= len;
    z /= len;

    const s = 1 + z * 0.1;
    return {
      x: cx + x * r * s,
      y: cy + y * r * s,
      z,
    };
  };

  const pathFromPts = (pts) => {
    let d = "";
    for (let i = 0; i < pts.length; i += 1) {
      const p = pts[i];
      d += `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`;
    }
    return d;
  };

  const meridians = [];
  const meridianCount = 24;
  for (let i = 0; i < meridianCount; i += 1) {
    const theta = (i / meridianCount) * Math.PI * 2;
    const pts = [];
    for (let j = 0; j <= 36; j += 1) {
      const phi = -Math.PI / 2 + (j / 36) * Math.PI;
      pts.push(project(theta, phi));
    }
    meridians.push({
      d: pathFromPts(pts),
      front: pts.reduce((s, p) => s + p.z, 0) / pts.length > -0.05,
    });
  }

  const parallels = [];
  const parallelCount = 14;
  for (let i = 1; i < parallelCount; i += 1) {
    const phi = -Math.PI / 2 + (i / parallelCount) * Math.PI;
    const pts = [];
    for (let j = 0; j <= 64; j += 1) {
      const theta = (j / 64) * Math.PI * 2;
      pts.push(project(theta, phi));
    }
    parallels.push({
      d: pathFromPts(pts),
      front: Math.cos(phi) > 0.08,
    });
  }

  const dots = [];
  for (let i = 0; i < meridianCount; i += 1) {
    const theta = (i / meridianCount) * Math.PI * 2;
    for (let j = 1; j < parallelCount; j += 1) {
      const phi = -Math.PI / 2 + (j / parallelCount) * Math.PI;
      const p = project(theta, phi);
      if (p.z > -0.05) {
        dots.push(p);
      }
    }
  }

  return { meridians, parallels, dots };
}

/** Quick light aiming presets (Photoroom compass). */
export const LIGHT_DIRECTION_PRESETS = [
  { id: "front", label: "Front", directionDeg: 180, poseDeg: 38, apiDirection: "front" },
  { id: "behind", label: "Behind", directionDeg: 0, poseDeg: 38, apiDirection: "back" },
  { id: "left", label: "Left", directionDeg: 90, poseDeg: 42, apiDirection: "left" },
  { id: "right", label: "Right", directionDeg: 270, poseDeg: 42, apiDirection: "right" },
  { id: "top", label: "Top", directionDeg: 180, poseDeg: 90, apiDirection: "front" },
  { id: "topLeft", label: "Top left", directionDeg: 115, poseDeg: 72, apiDirection: "left" },
  { id: "topRight", label: "Top right", directionDeg: 245, poseDeg: 72, apiDirection: "right" },
  { id: "frontLeft", label: "Front left", directionDeg: 145, poseDeg: 35, apiDirection: "front" },
];

const STYLE_LIGHT = {
  daylight: {
    glow: "rgba(255, 214, 140, 0.55)",
    glowCore: "rgba(255, 236, 190, 0.95)",
    cast: "rgba(8, 6, 4, 0.92)",
    blur: 8,
    stretch: 1.75,
    distance: 1.2,
    castOpacityMul: 1,
    castW: 52,
    castH: 16,
  },
  soft_light: {
    glow: "rgba(210, 220, 255, 0.4)",
    glowCore: "rgba(255, 255, 255, 0.85)",
    cast: "rgba(0, 0, 0, 0.55)",
    blur: 22,
    stretch: 1.25,
    distance: 0.85,
    castOpacityMul: 0.9,
    castW: 68,
    castH: 24,
  },
  crisp: {
    glow: "rgba(255, 255, 255, 0.5)",
    glowCore: "rgba(255, 255, 255, 1)",
    cast: "rgba(0, 0, 0, 0.95)",
    blur: 2,
    stretch: 1.55,
    distance: 1.05,
    castOpacityMul: 1.1,
    castW: 44,
    castH: 12,
  },
  floating: {
    glow: "rgba(180, 190, 255, 0.35)",
    glowCore: "rgba(230, 235, 255, 0.8)",
    cast: "rgba(0, 0, 0, 0.7)",
    blur: 16,
    stretch: 0.7,
    distance: 0.2,
    castOpacityMul: 1,
    castW: 58,
    castH: 20,
  },
};

function NudgeBtn({ className, label, onClick, disabled }) {
  return (
    <button
      type="button"
      className={`astp-globe__nudge ${className}`}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      <span aria-hidden="true" />
    </button>
  );
}

/**
 * Relight-style light gizmo for AI Shadow.
 * Pointer = light position; ray aims at subject; cast from feet opposite light.
 */
const ShadowGlobe = ({
  directionDeg = 225,
  poseDeg = 70,
  auto = false,
  disabled = false,
  size = 260,
  sourceImageUrl = null,
  subjectLoading = false,
  styleId = "daylight",
  intensity = 70,
  subjectPx = 120,
  onChange,
  onAutoToggle,
}) => {
  const uid = useId().replace(/:/g, "");
  const rootRef = useRef(null);
  const draggingRef = useRef(false);

  const pad = 36;
  const globeR = (size - pad * 2) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const subjectSize = subjectPx;
  const lightR = globeR * 0.9;

  const light = STYLE_LIGHT[styleId] || STYLE_LIGHT.daylight;
  const intensity01 = clamp(intensity / 100, 0.15, 1);

  const handle = useMemo(
    () => lightDiscPosition(directionDeg, poseDeg, cx, cy, lightR),
    [directionDeg, poseDeg, cx, cy, lightR]
  );

  const clientToLocal = useCallback(
    (clientX, clientY) => {
      const el = rootRef.current;
      if (!el) return { x: cx, y: cy };
      const rect = el.getBoundingClientRect();
      return {
        x: ((clientX - rect.left) / rect.width) * size,
        y: ((clientY - rect.top) / rect.height) * size,
      };
    },
    [size, cx, cy]
  );

  const commitLight = useCallback(
    (next) => {
      if (disabled || typeof onChange !== "function") return;
      onChange({
        directionDeg: ((Math.round(next.directionDeg) % 360) + 360) % 360,
        poseDeg: clamp(Math.round(next.poseDeg), 0, 90),
      });
    },
    [disabled, onChange]
  );

  const commitFromPointer = useCallback(
    (clientX, clientY) => {
      const local = clientToLocal(clientX, clientY);
      commitLight(pointerToLight(local.x - cx, local.y - cy, lightR));
    },
    [clientToLocal, commitLight, cx, cy, lightR]
  );

  const nudge = (dDir, dPose) => {
    if (auto) onAutoToggle?.(false);
    commitLight({
      directionDeg: directionDeg + dDir,
      poseDeg: poseDeg + dPose,
    });
  };

  const onPointerDown = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    draggingRef.current = true;
    if (auto) onAutoToggle?.(false);
    commitFromPointer(e.clientX, e.clientY);
  };

  const onPointerMove = (e) => {
    if (!draggingRef.current || disabled) return;
    commitFromPointer(e.clientX, e.clientY);
  };

  const onPointerUp = () => {
    draggingRef.current = false;
  };

  const active = !auto && !disabled;

  const fabric = useMemo(
    () =>
      buildFabricMesh(
        cx,
        cy,
        globeR * 0.985,
        handle.x,
        handle.y
      ),
    [cx, cy, globeR, handle.x, handle.y]
  );

  const lightVx = handle.x - cx;
  const lightVy = handle.y - cy;
  const lightLen = Math.hypot(lightVx, lightVy) || 1;
  const lux = lightVx / lightLen;
  const luy = lightVy / lightLen;
  const rimT = clamp(lightLen / lightR, 0.15, 1);

  // Always aim cast from light — Auto only hides the lamp/beam, not the style preview.
  let castUx = -lux;
  let castUy = -luy;
  if (castUy < 0.12) castUy = 0.12 + Math.max(0, -castUy) * 0.35;
  const castNorm = Math.hypot(castUx, castUy) || 1;
  castUx /= castNorm;
  castUy /= castNorm;

  // Elevation drives length hard: Top (pose ~90) = short pool; Front (low pose) = long cast.
  const elevNorm = clamp(poseDeg / 90, 0, 1);
  const lengthFromElev = 0.55 + (1 - elevNorm) * 1.35;
  const castDist =
    (10 + rimT * 22) *
    light.distance *
    lengthFromElev *
    (0.7 + intensity01 * 0.4);

  // Floating keeps a contact pool but still leans opposite the light so
  // Top left / Top right read differently.
  const rawShadowAngle = (Math.atan2(castUy, castUx) * 180) / Math.PI;
  const shadowAngleDeg =
    styleId === "floating" ? rawShadowAngle * 0.4 : rawShadowAngle;
  const castOpacity = Math.min(
    0.95,
    (0.55 + intensity01 * 0.4) * (light.castOpacityMul ?? 1)
  );
  let castScaleX = Math.max(0.65, (castDist / 20) * light.stretch);
  castScaleX = Math.min(castScaleX, styleId === "daylight" ? 1.9 : 1.6);
  let castScaleY = 0.4 + (1 - rimT) * 0.18 + elevNorm * 0.12;
  if (styleId === "soft_light") {
    castScaleX = Math.min(1.4, Math.max(0.95, castScaleX * 0.9));
    castScaleY = Math.max(0.55, castScaleY);
  }
  if (styleId === "floating") {
    castScaleX = 0.75 + (1 - elevNorm) * 0.25 + intensity01 * 0.08;
    castScaleY = 0.7 + elevNorm * 0.1;
  }

  // Torch beam: cone from lamp toward subject hub.
  const towardX = cx - handle.x;
  const towardY = cy - handle.y;
  const towardLen = Math.hypot(towardX, towardY) || 1;
  const bx = towardX / towardLen;
  const by = towardY / towardLen;
  const px = -by;
  const py = bx;

  const beamReach = 0.78;
  const beamEndX = handle.x + towardX * beamReach;
  const beamEndY = handle.y + towardY * beamReach;
  // Wider when brighter or lower sun (more rim).
  const halfSpread =
    11 + intensity01 * 14 + (1 - poseDeg / 90) * 10;
  const outerSpread = halfSpread * 1.55;

  const coneCore = `${handle.x.toFixed(2)},${handle.y.toFixed(2)} ${(beamEndX + px * halfSpread).toFixed(2)},${(beamEndY + py * halfSpread).toFixed(2)} ${(beamEndX - px * halfSpread).toFixed(2)},${(beamEndY - py * halfSpread).toFixed(2)}`;
  const coneOuter = `${handle.x.toFixed(2)},${handle.y.toFixed(2)} ${(beamEndX + px * outerSpread).toFixed(2)},${(beamEndY + py * outerSpread).toFixed(2)} ${(beamEndX - px * outerSpread).toFixed(2)},${(beamEndY - py * outerSpread).toFixed(2)}`;

  // Soft core streak down the beam axis.
  const streakEndX = handle.x + towardX * 0.62;
  const streakEndY = handle.y + towardY * 0.62;

  const lampR = 4.25;

  const sphereGradId = `astp-globe-sphere-${uid}`;
  const shineGradId = `astp-globe-shine-${uid}`;
  const lightGradId = `astp-globe-light-${uid}`;
  const coneGradId = `astp-globe-cone-${uid}`;
  const coneEdgeGradId = `astp-globe-cone-edge-${uid}`;
  const streakGradId = `astp-globe-streak-${uid}`;
  const clipId = `astp-globe-clip-${uid}`;

  return (
    <div
      className={`astp-globe${disabled ? " is-disabled" : ""}${auto ? " is-auto" : ""}${sourceImageUrl ? " has-image" : ""}`}
      data-style={styleId}
    >
      <div className="astp-globe__frame" style={{ width: size, height: size }}>
        <NudgeBtn
          className="astp-globe__nudge--n"
          label="Nudge light up"
          disabled={disabled}
          onClick={() => nudge(0, 8)}
        />
        <NudgeBtn
          className="astp-globe__nudge--s"
          label="Nudge light down"
          disabled={disabled}
          onClick={() => nudge(0, -8)}
        />
        <NudgeBtn
          className="astp-globe__nudge--w"
          label="Nudge light left"
          disabled={disabled}
          onClick={() => nudge(12, 0)}
        />
        <NudgeBtn
          className="astp-globe__nudge--e"
          label="Nudge light right"
          disabled={disabled}
          onClick={() => nudge(-12, 0)}
        />

        <div
          className="astp-globe__stage"
          style={{ "--astp-globe-clip-r": `${globeR}px` }}
        >
          <svg
            ref={rootRef}
            className="astp-globe__svg"
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            role="application"
            aria-label="Light direction"
            aria-valuetext={
              auto
                ? "Auto"
                : `Direction ${directionDeg} degrees, elevation ${poseDeg} degrees`
            }
            tabIndex={disabled ? -1 : 0}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onKeyDown={(e) => {
              if (disabled) return;
              const step = e.shiftKey ? 15 : 5;
              if (e.key === "ArrowLeft") {
                e.preventDefault();
                if (auto) onAutoToggle?.(false);
                nudge(step, 0);
              } else if (e.key === "ArrowRight") {
                e.preventDefault();
                if (auto) onAutoToggle?.(false);
                nudge(-step, 0);
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                if (auto) onAutoToggle?.(false);
                nudge(0, step);
              } else if (e.key === "ArrowDown") {
                e.preventDefault();
                if (auto) onAutoToggle?.(false);
                nudge(0, -step);
              }
            }}
          >
            <defs>
              <clipPath id={clipId}>
                <circle cx={cx} cy={cy} r={globeR} />
              </clipPath>
              {/* Matte dark stage — not a glowing ball */}
              <radialGradient id={sphereGradId} cx="50%" cy="42%" r="72%">
                <stop offset="0%" stopColor="#14151a" />
                <stop offset="70%" stopColor="#0b0c10" />
                <stop offset="100%" stopColor="#050507" />
              </radialGradient>
              {/* Tiny local spill near the lamp only */}
              <radialGradient
                id={shineGradId}
                cx={`${((handle.x / size) * 100).toFixed(1)}%`}
                cy={`${((handle.y / size) * 100).toFixed(1)}%`}
                r="28%"
              >
                <stop offset="0%" stopColor={light.glowCore} stopOpacity="0.14" />
                <stop offset="55%" stopColor={light.glow} stopOpacity="0.05" />
                <stop offset="100%" stopColor="rgba(0,0,0,0)" stopOpacity="0" />
              </radialGradient>
              <radialGradient id={lightGradId} cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={light.glowCore} stopOpacity="0.85" />
                <stop offset="45%" stopColor={light.glow} stopOpacity="0.28" />
                <stop offset="100%" stopColor={light.glow} stopOpacity="0" />
              </radialGradient>
              <linearGradient
                id={coneGradId}
                x1={handle.x}
                y1={handle.y}
                x2={beamEndX}
                y2={beamEndY}
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor={light.glowCore} stopOpacity="0.72" />
                <stop offset="40%" stopColor={light.glowCore} stopOpacity="0.38" />
                <stop offset="100%" stopColor={light.glow} stopOpacity="0" />
              </linearGradient>
              <linearGradient
                id={coneEdgeGradId}
                x1={handle.x}
                y1={handle.y}
                x2={beamEndX}
                y2={beamEndY}
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor={light.glowCore} stopOpacity="0.55" />
                <stop offset="100%" stopColor={light.glowCore} stopOpacity="0" />
              </linearGradient>
              <linearGradient
                id={streakGradId}
                x1={handle.x}
                y1={handle.y}
                x2={streakEndX}
                y2={streakEndY}
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
                <stop offset="70%" stopColor={light.glowCore} stopOpacity="0.35" />
                <stop offset="100%" stopColor={light.glowCore} stopOpacity="0" />
              </linearGradient>
            </defs>

            <circle
              cx={cx}
              cy={cy}
              r={globeR}
              fill={`url(#${sphereGradId})`}
              className="astp-globe__shell"
            />

            <g clipPath={`url(#${clipId})`} pointerEvents="none">
              {/* Back fabric */}
              {fabric.meridians.map((m, i) =>
                m.front ? null : (
                  <path
                    key={`mer-b-${i}`}
                    d={m.d}
                    className="astp-globe__fabric astp-globe__fabric--back"
                  />
                )
              )}
              {fabric.parallels.map((p, i) =>
                p.front ? null : (
                  <path
                    key={`par-b-${i}`}
                    d={p.d}
                    className="astp-globe__fabric astp-globe__fabric--back"
                  />
                )
              )}

              {/* Front matrix mesh — quiet lines, not emissive */}
              {fabric.meridians.map((m, i) =>
                m.front ? (
                  <path
                    key={`mer-f-${i}`}
                    d={m.d}
                    className="astp-globe__fabric"
                  />
                ) : null
              )}
              {fabric.parallels.map((p, i) =>
                p.front ? (
                  <path
                    key={`par-f-${i}`}
                    d={p.d}
                    className="astp-globe__fabric"
                  />
                ) : null
              )}

              {fabric.dots.map((d, i) => (
                <circle
                  key={`dot-${i}`}
                  cx={d.x}
                  cy={d.y}
                  r={0.9}
                  className="astp-globe__fabric-dot"
                  opacity={0.12 + ((d.z + 1) * 0.5) * 0.28}
                />
              ))}

              {!auto ? (
                <g className="astp-globe__torch">
                  {/* Local spill on stage only */}
                  <circle
                    cx={handle.x}
                    cy={handle.y}
                    r={22 + intensity01 * 8}
                    fill={`url(#${shineGradId})`}
                  />
                  <polygon
                    points={coneOuter}
                    fill={`url(#${coneGradId})`}
                    opacity={0.28 + intensity01 * 0.18}
                    className="astp-globe__cone-outer"
                  />
                  <polygon
                    points={coneCore}
                    fill={`url(#${coneGradId})`}
                    opacity={0.7 + intensity01 * 0.2}
                    className="astp-globe__cone"
                  />
                  {/* Edge lines so the cone reads as a beam */}
                  <line
                    x1={handle.x}
                    y1={handle.y}
                    x2={beamEndX + px * halfSpread}
                    y2={beamEndY + py * halfSpread}
                    stroke={`url(#${coneEdgeGradId})`}
                    strokeWidth={1}
                    opacity={0.55}
                  />
                  <line
                    x1={handle.x}
                    y1={handle.y}
                    x2={beamEndX - px * halfSpread}
                    y2={beamEndY - py * halfSpread}
                    stroke={`url(#${coneEdgeGradId})`}
                    strokeWidth={1}
                    opacity={0.55}
                  />
                  <line
                    x1={handle.x}
                    y1={handle.y}
                    x2={streakEndX}
                    y2={streakEndY}
                    stroke={`url(#${streakGradId})`}
                    strokeWidth={2}
                    strokeLinecap="round"
                    opacity={0.9}
                  />
                </g>
              ) : null}
            </g>

            <circle
              cx={cx}
              cy={cy}
              r={globeR}
              className="astp-globe__rim"
              fill="none"
            />

            {active ? (
              <g className="astp-globe__lamp" pointerEvents="none">
                <circle
                  cx={handle.x}
                  cy={handle.y}
                  r={12 + intensity01 * 6}
                  fill={`url(#${lightGradId})`}
                  opacity={0.55}
                  className="astp-globe__light-bloom"
                />
                <circle
                  cx={handle.x}
                  cy={handle.y}
                  r={lampR + 1.5}
                  className="astp-globe__lamp-rim"
                />
                <circle
                  cx={handle.x}
                  cy={handle.y}
                  r={lampR}
                  className="astp-globe__lamp-core"
                  style={{ fill: light.glowCore }}
                />
                <circle
                  cx={handle.x}
                  cy={handle.y}
                  r={lampR * 0.38}
                  className="astp-globe__lamp-hot"
                />
              </g>
            ) : null}
          </svg>

          <div
            className="astp-globe__subject"
            style={{
              width: subjectSize,
              height: subjectSize,
              "--astp-cast-color": light.cast,
              "--astp-cast-blur": `${light.blur * (0.75 + intensity01 * 0.5)}px`,
              "--astp-cast-opacity": String(castOpacity),
              "--astp-cast-angle": `${shadowAngleDeg.toFixed(1)}deg`,
              "--astp-cast-scale-x": String(castScaleX.toFixed(3)),
              "--astp-cast-scale-y": String(castScaleY.toFixed(3)),
              "--astp-cast-w": `${light.castW || 56}px`,
              "--astp-cast-h": `${light.castH || 16}px`,
            }}
          >
            {/* Floor plate so black cast reads on the dark sphere */}
            <div className="astp-globe__floor" aria-hidden="true" />
            <div className="astp-globe__cast" aria-hidden="true" />
            <div className="astp-globe__subject-spin">
              {subjectLoading ? (
                <div className="astp-globe__subject-placeholder">
                  Cutting…
                </div>
              ) : sourceImageUrl ? (
                <img
                  className="astp-globe__subject-img"
                  src={sourceImageUrl}
                  alt=""
                  draggable={false}
                />
              ) : (
                <div className="astp-globe__subject-placeholder">Subject</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="astp-globe__readout" aria-live="polite">
        {auto ? (
          <span>Auto light</span>
        ) : (
          <>
            <span>
              Direction <strong>{directionDeg}°</strong>
            </span>
            <span className="astp-globe__dot" aria-hidden="true">
              ·
            </span>
            <span>
              Elevation <strong>{poseDeg}°</strong>
            </span>
          </>
        )}
      </div>

      <div className="astp-globe__actions">
        <button
          type="button"
          className={`astp-globe__auto-btn${auto ? " is-selected" : ""}`}
          disabled={disabled}
          aria-pressed={auto}
          onClick={() => onAutoToggle?.(!auto)}
        >
          Auto
        </button>
        <button
          type="button"
          className="astp-globe__reset-btn"
          disabled={disabled || auto}
          onClick={() => {
            if (auto) onAutoToggle?.(false);
            commitLight({ directionDeg: 225, poseDeg: 70 });
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default ShadowGlobe;
